import { FormArray, FormGroup } from "@angular/forms";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { FormlyFieldProps, FormlyFormOptions, FormlyHookConfig } from "@ngx-formly/core/lib/models";
import { DateTime } from "luxon";
import { distinctUntilChanged, map, Observable, tap } from "rxjs";
import { EFormWebServiceService } from "../../../services/eform/e-form-webservice.service";
import { BooleanItemMetadata } from "../form-metadata/item/boolean-item-metadata.model";
import { DataSourceItemMetadata } from "../form-metadata/item/datasource-item-metadata.model";
import { ItemMetadata } from "../form-metadata/item/item-metadata.model";
import { LengthItemMetadata } from "../form-metadata/item/length-item-metadata.model";
import { TimeItemMetadata } from "../form-metadata/item/time-item-metadata.model";
import { ValueItemMetadata } from "../form-metadata/item/value-item-metadata.model";
import { LayoutTypeMetadata } from "../form-metadata/layout-type-metadata.enum";
import { ExtendedLayoutMetadata } from "../form-metadata/layout/extends-layout.metadata.model";
import { LayoutMetadata } from "../form-metadata/layout/layout-metadata.model";
import { RepeatLayoutMetadata } from "../form-metadata/layout/repeat-layout-metadata.model";
import { RowLayoutMetadata } from "../form-metadata/layout/row-layout.metadata.model";
import { SectionLayoutMetadata } from "../form-metadata/layout/section-layout.model";
import { TableLayoutMetadata } from "../form-metadata/layout/table-layout-metadata.model";
import { WebServiceLayoutMetadata } from "../form-metadata/layout/webservice-layout-metadata.model";
import { HasShowHideLogic } from "../form-metadata/logic/has-show-hide-logic.model";
import { Metadata } from "../form-metadata/metadata.model";


export interface FieldConfigRegistry {
    register(id: string, field: FormlyFieldConfig): void
    get(id: string): FormlyFieldConfig | undefined
    delete(id: string): void
    callWebService(): EFormWebServiceService
    setCustomDataWebService(customData: Map<string, any>): void
    getCustomDataWebService(): Map<string, any>
}

export interface FieldConfigFactory {
    newRowOrLayoutConfig(metadata: Metadata): FormlyFieldConfig
    newRowFieldConfig(metadata: LayoutMetadata): RowLayoutFormlyFieldGroup
    newLayoutFieldConfig(metadata: ExtendedLayoutMetadata): ExtendedLayoutFormlyFieldGroup
    newSectionFieldConfig(metadata: SectionLayoutMetadata): SectionLayoutFormlyFieldGroup
    newItemFieldConfig(metadata: Metadata, className: string): FormlyFieldConfig
    registry(): FieldConfigRegistry
}

export interface ExtendedFormlyFieldProps extends FormlyFieldProps {

    idexpand?: string,
    hideHeader?: boolean,
    onFormCreation?: boolean,
    label2?: string,
    safeHtml?: boolean,
    label2Title?: boolean,
    label2UpperDivider?: boolean,
    autosize?: boolean,
    appearance?: string,
    mappingData?: WebServiceDataSourceFormlyFieldProp
    addWS?: () => void
    removeWS?: (i: number) => void

}

export interface WebServiceDataSourceFormlyFieldProp {

    doAction: string,
    action: WebServiceDataSourceActionFormlyFieldProp[]

}

export interface WebServiceDataSourceActionFormlyFieldProp {

    value: string,
    endpoint: WebServiceDataSourceActionEndpointFormlyFieldProp,
    mapping: Record<string, string>

}

export interface WebServiceDataSourceActionEndpointFormlyFieldProp {

    url: string,
    method: "POST" | "GET",
    filter: string,
    params: WebServiceDataSourceActionEndpointParamsFormlyFieldProp[],
    customData: Record<string, any>

}

export interface WebServiceDataSourceActionEndpointParamsFormlyFieldProp {

    name: string,
    source: "fn" | "field",
    value: string

}

export class FormlyFieldConfigRegistry implements FieldConfigRegistry {

    constructor(protected eFormWebServiceService: EFormWebServiceService) { }

    protected formlyFieldConfigMap: Map<string, FormlyFieldConfig> = new Map()

    protected customData!: Map<string, any>

    register(id: string, field: FormlyFieldConfig): void {
        this.formlyFieldConfigMap.set(id, field)
    }

    get(id: string): FormlyFieldConfig | undefined {
        return this.formlyFieldConfigMap.get(id)
    }

    delete(id: string): void {
        this.formlyFieldConfigMap.delete(id)
    }

    callWebService(): EFormWebServiceService {
        return this.eFormWebServiceService
    }

    setCustomDataWebService(customData: Map<string, any>) {
        this.customData = customData
    } 

    getCustomDataWebService(): Map<string, any> {
        return this.customData
    }

}

export class FormView {

    constructor(metadata: SectionLayoutFormlyFieldGroup) {
        this.options.formState[metadata.props.idexpand!] = true
    }

    public form = new FormGroup({});
    public options: FormlyFormOptions = {
        formState: {},
    };

    public fields: FormlyFieldConfig[] = []
    public model: any = {}

    reset() {
        this.fields.splice(0, this.fields.length)
        this.model = {}
        this.options = {
            formState: {}
        }
    }

}

export class FormlyFieldConfigFactory implements FieldConfigFactory {


    constructor(protected _registry: FieldConfigRegistry) {

    }

    registry(): FieldConfigRegistry {
        return this._registry
    }

    newRowOrLayoutConfig(metadata: Metadata): FormlyFieldConfig {
        switch (metadata.type) {
            case LayoutTypeMetadata.Row:
                return this.newRowFieldConfig(<RowLayoutMetadata>metadata)
            case LayoutTypeMetadata.Repeat:
                return this.newLayoutFieldConfig(<RepeatLayoutMetadata>metadata)
            case LayoutTypeMetadata.Table:
                return this.newLayoutFieldConfig(<TableLayoutMetadata>metadata)
            case LayoutTypeMetadata.WebService:
                return this.newLayoutFieldConfig(<WebServiceLayoutMetadata>metadata)
        }
        return this.newRowFieldConfig(<RowLayoutMetadata>metadata)
    }

    newRowFieldConfig(metadata: RowLayoutMetadata): RowLayoutFormlyFieldGroup {
        return new RowLayoutFormlyFieldGroup(metadata, this)
    }

    newLayoutFieldConfig(metadata: ExtendedLayoutMetadata): ExtendedLayoutFormlyFieldGroup {

        if (metadata.type == LayoutTypeMetadata.Repeat) {
            return new RepeatLayoutFormlyFieldGroup(<RepeatLayoutMetadata>metadata, this)
        }

        if (metadata.type == LayoutTypeMetadata.WebService) {
            return new WebServiceLayoutFormlyFieldGroup(<WebServiceLayoutMetadata>metadata, this)
        }

        return new TableLayoutFormlyFieldGroup(<TableLayoutMetadata>metadata, this)

    }

    newSectionFieldConfig(metadata: SectionLayoutMetadata, className?: string): SectionLayoutFormlyFieldGroup {
        return new SectionLayoutFormlyFieldGroup(metadata, this, className)
    }

    newItemFieldConfig(metadata: Metadata, className: string): FormlyFieldConfig {

        switch (metadata.type) {
            case "BUTTON_ITEM":
                return new ItemFormlyFieldConfig(<ItemMetadata>metadata, className, this)
            case "NUMERIC_ITEM":
                return new NumberInputFormlyFieldConfig(<ItemMetadata>metadata, className, this)
            case "TEXT_ITEM":
                return new InputItemFormlyFieldConfig(<LengthItemMetadata>metadata, className, this)
            case "MULTI_CHECKBOX_ITEM":
                return new RadioInputItemFormlyFieldConfig(<DataSourceItemMetadata>metadata, className, this)
            case "RADIO_ITEM":
                return new RadioInputItemFormlyFieldConfig(<DataSourceItemMetadata>metadata, className, this)
            case "TEXTAREA_ITEM":
                return new TextareaItemFormlyFieldConfig(<LengthItemMetadata>metadata, className, this)
            case "SELECT_ITEM":
                return new SelectInputItemFormlyFieldConfig(<DataSourceItemMetadata>metadata, className, this)
            case "CHECKBOX_ITEM":
                return new CheckboxItemFormlyFieldConfig(<BooleanItemMetadata>metadata, className, this)
            case "DATEPICKER_ITEM":
                return new DatePickerInputFormlyFieldConfig(<ItemMetadata>metadata, className, this)
            case "HTML_ITEM":
                return new HtmlItemFormlyFieldConfig(<ValueItemMetadata>metadata, className, this)
            case "TIME_ITEM":
                return new TimeItemFormlyFieldConfig(<TimeItemMetadata>metadata, className, this)
            case "CUSTOM_ITEM":
                return new InputItemFormlyFieldConfig(<LengthItemMetadata>metadata, className, this)
        }
        return new InputItemFormlyFieldConfig(<LengthItemMetadata>metadata, className, this)

    }
}

export class SectionLayoutFormlyFieldGroup implements FormlyFieldConfig {

    metadata: SectionLayoutMetadata
    id?: string;
    className?: string = '';

    wrappers: string[] = ['section']
    fieldGroup: FormlyFieldConfig[] = []
    props: ExtendedFormlyFieldProps = {
        idexpand: "",
        hideHeader: false,
        onFormCreation: true
    }

    constructor(metadata: SectionLayoutMetadata, protected factory: FieldConfigFactory, className?: string) {
        this.metadata = metadata
        if (className) {
            this.className = className
        }
        this.refresh()
        this.inflate()
    }

    refresh() {
        this.props.label = this.metadata.name
        this.props.idexpand = this.metadata.id
        this.props.hideHeader = this.metadata.hideHeader
        this.props.onFormCreation = this.metadata.onFormCreation
        this.id = this.metadata.id
    }


    inflate(): void {
        this.fieldGroup.splice(0, this.fieldGroup.length)
        this.metadata.items.forEach(item => {
            let formlyRow = this.factory.registry().get(item.id)
            if (formlyRow == null) {
                formlyRow = this.factory.newRowOrLayoutConfig(item)
            }
            this.fieldGroup.push(formlyRow)
            this.factory.registry().register(<string>formlyRow.id, formlyRow)
        })
        //Register him self because is the root node
        this.factory.registry().register(<string>this.id, this)
    }

}

export class RowLayoutFormlyFieldGroup implements FormlyFieldConfig {
    metadata: RowLayoutMetadata
    id?: string;

    fieldGroupClassName?: string = 'row2';
    fieldGroup: FormlyFieldConfig[] = []

    constructor(metadata: RowLayoutMetadata, protected factory: FieldConfigFactory) {
        this.metadata = metadata
        this.refresh()
        this.inflate()
    }

    refresh() {
        this.id = this.metadata.id
    }


    inflate(className: string = 'colmax'): void {
        this.fieldGroup.splice(0, this.fieldGroup.length)
        this.metadata.items.forEach(item => {
            let formlyInput = this.factory.registry().get(item.id)
            if (formlyInput == null) {
                formlyInput = this.factory.newItemFieldConfig(item, className)
            }
            this.fieldGroup.push(formlyInput)
            this.factory.registry().register(<string>formlyInput.id, formlyInput)
        })
        //Register him self because is the root node
        this.factory.registry().register(<string>this.id, this)
    }
}

export class ExtendedLayoutFormlyFieldGroup implements FormlyFieldConfig {
    metadata: ExtendedLayoutMetadata
    type!: string
    key?: string;
    id?: string;
    className: string = ''
    hide?: boolean

    fieldArray: FormlyFieldConfig = {
        fieldGroup: [] = []
    };

    props: ExtendedFormlyFieldProps = {
    }

    constructor(metadata: ExtendedLayoutMetadata, protected factory: FieldConfigFactory) {
        this.metadata = metadata
        this.refresh()
    }

    refresh() {
        this.key = this.metadata.fieldName
        this.props.label = this.metadata.name
        this.id = this.metadata.id
    }

    showSelected() {
        this.className += " colmax-selected un-colmax-repeat"
    }

    clearSelection() {
        this.className = this.className.replace(" colmax-selected un-colmax-repeat", "")
    }


    inflate(): void {
        this.fieldArray.fieldGroup?.splice(0, this.fieldArray.fieldGroup.length)
        this.metadata.items.forEach(item => {
            let formlyRow = this.factory.registry().get(item.id)
            if (formlyRow == null) {
                formlyRow = this.factory.newRowFieldConfig(item)
            }
            this.fieldArray.fieldGroup?.push(formlyRow)
            this.factory.registry().register(<string>formlyRow.id, formlyRow)
        })
        //Register him self because is the root node
        this.factory.registry().register(<string>this.id, this)
    }

}

export class RepeatLayoutFormlyFieldGroup extends ExtendedLayoutFormlyFieldGroup {

    constructor(metadata: RepeatLayoutMetadata, factory: FieldConfigFactory) {
        super(metadata, factory)
        super.inflate()
        this.type = "repeat"
    }

}

export class TableLayoutFormlyFieldGroup extends ExtendedLayoutFormlyFieldGroup {

    constructor(metadata: TableLayoutMetadata, factory: FieldConfigFactory) {
        super(metadata, factory)
        super.inflate()
        this.type = "table"
    }

}

export class WebServiceLayoutFormlyFieldGroup extends ExtendedLayoutFormlyFieldGroup {

    override metadata: WebServiceLayoutMetadata

    hooks?: FormlyHookConfig

    fieldGroup: FormlyFieldConfig[] = []

    constructor(metadata: WebServiceLayoutMetadata, factory: FieldConfigFactory) {
        super(metadata, factory)
        this.fieldArray = <any> undefined
        this.type = "webservice"
        this.metadata = metadata
        this.hide = true
        this.inflate()
        this.refresh()
    }

    override inflate() {
        this.fieldGroup.splice(0, this.fieldGroup.length)
        this.metadata.items.forEach(item => {
            let formlyRow = this.factory.registry().get(item.id)
            if (formlyRow == null) {
                formlyRow = this.factory.newRowOrLayoutConfig(item)
            }
            this.fieldGroup.push(formlyRow)
            this.factory.registry().register(<string>formlyRow.id, formlyRow)
        })
        //Register him self because is the root node
        this.factory.registry().register(<string>this.id, this)
    }

    override async refresh() {
        super.refresh()

        if (this.metadata.datasource) {
            this.props.mappingData = JSON.parse(this.metadata.datasource)

            let mappingResult = (result: ParsedResult, key: string) => {

                if (key == 'result' && result.type === 'string') {
                    return result.value['result']
                }

                // result.0.ine10 example if result is in array, you can use .0. or .1. etc etc //
                return key.replace(/\[(\w+)\]/g, '.$1').split('.').reduce(
                    (acc, currentKey) => {
                        if (acc == null || !(currentKey in acc)) 
                            throw new Error(`currentKey not found ${currentKey} in ${JSON.stringify(result.value)} of ${key}`)
                        return acc?.[currentKey]
                    }, 
                result.value);
            } 

            let doMapping = (action: WebServiceDataSourceActionFormlyFieldProp, field: FormlyFieldConfig, result: ParsedResult ) => {

                const formControl = field.formControl

                if (!formControl) {
                    field.props!['showError'].set(true)
                    return
                }

                if (field instanceof WebServiceLayoutFormlyFieldGroup) {
                    if (field.fieldGroup) {
                        for (let row of field.fieldGroup) {
                            if (row instanceof RowLayoutFormlyFieldGroup) {
                                for (let f of row.fieldGroup) {
                                    let key = action.mapping[<string>f.key]
                                    if (key) {
                                        let value = mappingResult(result, key)
                                        formControl.get(<string>f.key)?.setValue(value)
                                        //console.log(value)
                                    }
                                }
                            }
                        }
                    }
                }

                field.props!['showError'].set(false)

            }

            let doAction = (field: FormlyFieldConfig, val: any) => {

                let action = this.props.mappingData?.action.find((a) => a.value === val)
                if (action == undefined) {
                    field.hide = true
                    return
                }

                field.hide = false

                const parentModel = field.parent?.model

                // Currently only doAction fields can be added to params filter //
                // Change this doAction to array if u need check more than one value //

                let filter: WebServiceDataSourceActionEndpointFormlyFieldProp = {
                    url: action.endpoint.url,
                    method: action.endpoint.method,
                    filter: action.endpoint.filter,
                    params: action.endpoint.params.map(p => {
                        if (p.source === 'field' && this.props.mappingData?.doAction === p.value) {
                            let value = parentModel[p.value]
                            return {
                                ...p,
                                value
                            }
                        }

                        return { ...p }
                    }),
                    customData: Object.fromEntries(this.factory.registry().getCustomDataWebService())
                }

                this.factory.registry().callWebService().call(filter).subscribe({
                    next: (r) => {

                        try {

                            if (!r.ok) {
                                console.log("error ws ", r.messageCode)
                                field.props!['showError'].set(true)
                                return
                            }

                            const result = detectTextOrJson(r.entity)
                            doMapping(action, field, result)

                        } catch (error) {
                            console.log(error)
                            field.props!['showError'].set(true)
                        }
                    },
                    error: (error) => {
                        console.log(error)
                        field.props!['showError'].set(true)
                    }
                })

            }

            this.hooks = {
                onInit: (field) => {
                    let fieldAction = findFieldAction(this.props.mappingData?.doAction, field)
                    if (fieldAction == null) {
                        field.hide = true
                        return
                    }

                    field.hide = field.model ? false : true

                    return fieldAction.formControl?.valueChanges.pipe(tap(val => {
                        doAction(field, val)
                    }))
                }
            }
        }

    }

}

export class ItemFormlyFieldConfig implements FormlyFieldConfig {
    metadata: ItemMetadata

    id?: string;
    type?: string;
    key?: string;
    defaultValue?: any
    className: string = ''
    wrappers: string[] = []
    hideExpression?: boolean | string | ((model: any, formState: any, field?: FormlyFieldConfig) => boolean);
    validators?: any;
    expressionProperties?: {
        [property: string]: string | ((model: any, formState: any, field?: FormlyFieldConfig) => any) | Observable<any>;
    };

    props: ExtendedFormlyFieldProps = {
        label2: ""
    }

    constructor(metadata: ItemMetadata, className: string, registry: FieldConfigFactory) {
        this.metadata = metadata
        this.className = className
        this.refresh()
    }

    showSelected() {
        this.className += " colmax-selected"
    }

    clearSelection() {
        this.className = this.className.replace(" colmax-selected", "")
    }

    refresh() {

        this.id = this.metadata.id
        let type = convertItemTypeMetadataToFormlyType(this.metadata.type)
        if (type) {
            this.type = type
        }

        this.key = this.metadata.fieldName
        this.props.label2 = this.metadata.fieldLabel
        this.props.required = this.metadata.required

        if (this.metadata.readonly) {
            this.props.readonly = this.metadata.readonly
        }

        if (this.metadata.hiddenExpression != null) {
            this.hideExpression = this.metadata.hiddenExpression
        }

        if (this.metadata.expressionProperties != null) {
            let obj: any = {}
            let name: string = "model." + this.metadata.fieldName
            obj[name] = this.metadata.expressionProperties
            this.expressionProperties = obj
        }

        if (this.metadata.hint != null) {
            this.props.description = this.metadata.hint
        }

        if (this.metadata.validatorName != null && this.metadata.validatorName != "") {
            this.validators = { validation: [this.metadata.validatorName] }
            //this.validators.validation = [this.metadata.validatorName]
        }

        if (this.metadata.defaultValue) {
            this.defaultValue = this.metadata.defaultValue
        }

    }
}

export class InputItemFormlyFieldConfig extends ItemFormlyFieldConfig {

    override wrappers: string[] = ['label', 'form-field']
    override metadata: LengthItemMetadata
    constructor(metadata: LengthItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)

        this.metadata = metadata
        this.refresh()
    }

    override refresh() {
        super.refresh()
        this.props.maxLength = this.metadata.maxLength
    }

}

export class NumberInputFormlyFieldConfig extends ItemFormlyFieldConfig {

    override wrappers: string[] = ['label', 'form-field']
    override metadata: ItemMetadata
    constructor(metadata: ItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)

        this.metadata = metadata
        this.refresh()
    }

    override refresh() {
        super.refresh()
    }

}

export class DatePickerInputFormlyFieldConfig extends ItemFormlyFieldConfig {

    override wrappers: string[] = ['label', 'form-field']
    override metadata: ItemMetadata
    override defaultValue?: any;

    hooks?: FormlyHookConfig;

    constructor(metadata: ItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)

        this.metadata = metadata
        this.defaultValue = this.metadata.defaultValue
        this.refresh()
    }

    override refresh() {
        super.refresh()

        this.hooks = {
            onInit: (field) => {
                return field.formControl?.valueChanges.pipe(
                    tap(val => {
                        if (val instanceof DateTime) {
                            const formatted = val.toFormat('yyyy-MM-dd')
                            if (field.formControl?.value !== formatted) {
                                field.formControl?.setValue(formatted, { emitEvent: false })
                            }
                        }
                    }))
            }
        }
    }

}

export class TimeItemFormlyFieldConfig extends ItemFormlyFieldConfig {

    override wrappers: string[] = ['label', 'form-field']
    override metadata: TimeItemMetadata
    constructor(metadata: TimeItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)

        this.metadata = metadata
        this.refresh()

    }

    override refresh() {
        super.refresh()
    }

}

export class TextareaItemFormlyFieldConfig extends InputItemFormlyFieldConfig {

    constructor(metadata: LengthItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)
        this.refresh()
    }

    override refresh() {
        super.refresh()
        this.props.maxLength = this.metadata.maxLength
        this.props.autosize = true
        this.props.rows = 5
    }

}

export class CheckboxItemFormlyFieldConfig extends ItemFormlyFieldConfig {

    override metadata: BooleanItemMetadata
    override wrappers: string[] = ['label'/*, 'form-field'*/]
    override defaultValue?: any
    hooks?: FormlyHookConfig


    constructor(metadata: BooleanItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)
        this.className = this.className
        this.metadata = metadata
        this.refresh();
        this.defaultValue = this.metadata.defaultValue
        //(<any>this).id = null;
    }

    override refresh() {
        super.refresh()
        this.props.appearance = ''
        this.props.label = this.metadata.fieldLabel

        if (this.metadata.defaultValue == undefined || this.metadata.defaultValue == null) {
            this.metadata.defaultValue = false
        }

        this.defaultValue = this.metadata.defaultValue

        if (this.metadata.logicShowHideField != null) {
            this.hooks = {
                onInit: (field) => {

                    let key = `${field.key}`

                    displayFields(this.metadata, field, field.model[key])

                    return field.formControl?.valueChanges.pipe(
                        tap(() => {
                            displayFields(this.metadata, field, field.model[key])
                        })
                    )
                }
            }
        }
    }
}

export class SelectInputItemFormlyFieldConfig extends ItemFormlyFieldConfig {

    override metadata: DataSourceItemMetadata
    override wrappers: string[] = ['label', 'form-field']
    hooks?: FormlyHookConfig;


    constructor(metadata: DataSourceItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)

        this.metadata = metadata
        this.refresh()

    }

    override refresh() {
        super.refresh()
        this.props.options = []

        this.metadata.options.forEach(option => {
            let op = { label: option.value, value: option.id }
            let array: any[] = <any[]>this.props.options
            array.push(op)
        })

        if (this.metadata.parentSelectFieldName != null || this.metadata.logicShowHideField != null) {
            this.hooks = {
                onInit: (field) => {

                    if (this.metadata.parentSelectFieldName != null) {
                        parentSelectField(this.metadata, field)
                    }

                    if (this.metadata.logicShowHideField != null) {
                        let key = `${field.key}`

                        displayFields(this.metadata, field, field.model[key])

                        return field.formControl?.valueChanges.pipe(
                            tap(() => {
                                displayFields(this.metadata, field, field.model[key])
                            })
                        )
                    }

                    return
                },
            }
        }
    }
}

export class RadioInputItemFormlyFieldConfig extends SelectInputItemFormlyFieldConfig {

    override wrappers: string[] = ['label'/*, 'form-field'*/]

    constructor(metadata: DataSourceItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)
        this.className = this.className + " radio-fm";

        //(<any>this).id = null;
    }

    override refresh() {
        super.refresh()
        this.props.appearance = ''

        if (this.metadata.logicShowHideField != null) {
            this.hooks = {
                onInit: (field) => {

                    let key = `${field.key}`

                    displayFields(this.metadata, field, field.model[key])

                    return field.formControl?.valueChanges.pipe(
                        tap(() => {
                            displayFields(this.metadata, field, field.model[key])
                        })
                    )
                }
            }
        }
    }
}

export class HtmlItemFormlyFieldConfig extends ItemFormlyFieldConfig {

    override metadata: ValueItemMetadata

    override wrappers: string[] = ['label']

    template = ""
    override props: ExtendedFormlyFieldProps = {
        safeHtml: true,
        label2: "",
        label2Title: true,
        label2UpperDivider: true
    }

    constructor(metadata: ValueItemMetadata, className: string, registry: FieldConfigFactory) {
        super(metadata, className, registry)
        this.metadata = metadata
        this.refresh()

    }

    override refresh() {
        super.refresh()
        this.template = '<div class="ql-editor">' + this.metadata.value + '</div>'
        this.props.label2 = this.metadata.fieldLabel

    }
}

function convertItemTypeMetadataToFormlyType(type: string): string | null {
    switch (type) {
        case "BUTTON_ITEM":
            return "button"
        case "TEXT_ITEM":
            return "input"
        case "NUMERIC_ITEM":
            return "number"
        case "RADIO_ITEM":
            return "radio"
        case "TEXTAREA_ITEM":
            return "textarea"
        case "SELECT_ITEM":
            return "select"
        case "CHECKBOX_ITEM":
            return "checkbox"
        case "MULTI_CHECKBOX_ITEM":
            return "multicheckbox"
        case "DATEPICKER_ITEM":
            return "datepicker"
        case "TIME_ITEM":
            return "time"
        case "HTML_ITEM":
            return null
        case "CUSTOM_ITEM":
            return "custom"
    }
    return "text"
}

function parentSelectField(metadata: DataSourceItemMetadata, field: FormlyFieldConfig) {
    const parentControl = field.form?.get("" + metadata.parentSelectFieldName);
    (<any>field.props)['options'] = parentControl?.valueChanges.pipe(
        distinctUntilChanged(),
        map(parentId => {
            let currentControl = field.form?.get(metadata.fieldName)
            currentControl?.setValue(null);
            let array: any[] = []
            let dss = metadata.dependentOptions.get(parentId)
            dss?.forEach(p => {
                array.push({ label: p.value, value: p.id })
            })
            return array
        })
    )
}

function findFieldAction(fieldName: string | undefined, field: FormlyFieldConfig): FormlyFieldConfig | null {

    if (!fieldName) {
        return null
    }

    if (field.parent?.fieldGroup) {
        for (let row of field.parent?.fieldGroup) {
            if (row instanceof RowLayoutFormlyFieldGroup) {
                let field = row.fieldGroup?.find((f) => f.key === fieldName)
                if (field) {
                    return field
                }
            }
        }
    }

    return null
}

function displayFields(metadata: HasShowHideLogic, field: FormlyFieldConfig, value: any) {
    const fieldVisibility = new Map<FormlyFieldConfig, boolean>()

    metadata.logicShowHideField.forEach((logic) => {
        if (logic.ref == null || logic.ref == '') return

        let logicField: FormlyFieldConfig<FormlyFieldProps & { [additionalProperties: string]: any; }> | undefined

        if (field.parent?.parent?.fieldGroup) {
            for (let row of field.parent?.parent?.fieldGroup) {

                if (row instanceof RowLayoutFormlyFieldGroup) {
                    logicField = row.fieldGroup?.find((f) => f.key === logic.ref)
                    if (logicField != undefined) break
                }

                if (row instanceof ExtendedLayoutFormlyFieldGroup && row.key === logic.ref) {
                    logicField = row
                    break
                }

            }
        }

        if (logicField == undefined) return

        let visible = false
        if (metadata.type == 'MULTI_CHECKBOX_ITEM' && value != undefined) {
            visible = !!value[<string>logic.value]
        } else {
            visible = logic.value == value
        }

        fieldVisibility.set(logicField, (fieldVisibility.get(logicField) ?? false) || visible)
    })

    fieldVisibility.forEach((visible, logicField) => {
        logicField.hide = !visible
    })
}

type ParsedResult =
    | { type: 'object', value: Record<string, any> }
    | { type: 'string', value: Record<string, any> };

function detectTextOrJson(input: Record<string, any>): ParsedResult {

    try {
        const parsed = JSON.parse(input['result']);  

        if (parsed !== null && typeof parsed === 'object') {
            input['result'] = parsed
            return { type: 'object', value: input };
        }

    } catch (e) {
        // Ignore object is plain //
    }

    return { type: 'string', value: input };
}
